import { useQuery } from "@tanstack/react-query";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";

import { ChartPanel, DashboardsPageFrame, DataTable, KpiCard, KpiGrid } from "./dashboardKit";
import { dcmpBillingSnapshotQueryOptions } from "./dcmpBillingReactQuery";
import type {
  DcmpBillingGuideStatus,
  DcmpBillingSnapshotResponse,
  DcmpBillingWeeklyCohort,
  DcmpBillingWeeklyIneligible,
} from "./dcmpBillingTypes";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const COLORS = {
  orange: "#ff6b35",
  peach: "#f7c59f",
  navy: "#004e89",
  blue: "#1a659e",
};

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const integer = new Intl.NumberFormat("en-US");

const STATUS_COPY: Record<string, [string, string, string]> = {
  aligned: [
    "Approved for GUIDE",
    "Internally called “aligned”: CMS has approved the patient for GUIDE participation.",
    COLORS.navy,
  ],
  pending: [
    "Awaiting CMS decision",
    "Enrollment information has been sent and a CMS determination is pending.",
    COLORS.blue,
  ],
  submitted: ["Sent to CMS", "The patient enrollment has been submitted to CMS.", COLORS.orange],
  assessed: [
    "Assessment complete",
    "The clinical assessment is complete but enrollment is not final.",
    "#db8d59",
  ],
  new: [
    "Not yet assessed",
    "The patient has been added but has not completed assessment.",
    COLORS.peach,
  ],
  archived: [
    "Carespace closed",
    "The patient’s Carespace has been archived and is no longer active.",
    "#a4a48e",
  ],
  unaligned: ["Not participating", "The patient is not participating in GUIDE.", "#c6c6aa"],
  review: ["Needs review", "The patient’s GUIDE status requires operational review.", "#7d8fa0"],
};

const COHORT_SEGMENTS = [
  ["approved", "Approved for GUIDE", COLORS.navy],
  ["inProgress", "In progress", COLORS.blue],
  ["new", "Not yet assessed", COLORS.peach],
  ["closed", "Closed", COLORS.orange],
] as const;

const INELIGIBLE_SEGMENTS = [
  ["coverage", "Coverage", COLORS.orange],
  ["clinicalProgram", "Clinical / program", COLORS.navy],
  ["residence", "Residence", COLORS.blue],
  ["notRecorded", "Reason not recorded", COLORS.peach],
] as const;

function formatMonth(value: string, long = false): string {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-US", {
    month: long ? "long" : "short",
    year: "numeric",
  });
}

function formatWeek(value: string): string {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function delta(current: number | undefined, prior: number | undefined): number | null {
  if (current === undefined || prior === undefined || !prior) return null;
  return ((current - prior) / prior) * 100;
}

function formatDelta(value: number | null, fallback: string): string {
  if (value === null) return fallback;
  return `${value >= 0 ? "↑" : "↓"} ${Math.abs(value).toFixed(1)}% month over month`;
}

function StatusSummary({ rows }: { rows: DcmpBillingGuideStatus[] }) {
  const total = rows.reduce((sum, row) => sum + row.patients, 0);
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const normalizedStatus = String(row.status || "unknown");
        const [label, definition, color] = STATUS_COPY[normalizedStatus] || [
          normalizedStatus === "unknown" ? "Unknown" : normalizedStatus,
          "Current operational status.",
          "#7d8fa0",
        ];
        const width = total ? (row.patients / total) * 100 : 0;
        return (
          <div
            className="grid grid-cols-[140px_1fr_auto] items-center gap-3 text-sm"
            key={normalizedStatus}
          >
            <span className="truncate font-medium text-foreground" title={definition}>
              {label}
            </span>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full"
                style={{ width: `${width}%`, background: color }}
              />
            </div>
            <strong className="tabular-nums text-foreground">{integer.format(row.patients)}</strong>
          </div>
        );
      })}
    </div>
  );
}

function CohortChart({ rows }: { rows: DcmpBillingWeeklyCohort[] }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {COHORT_SEGMENTS.map(([key, label, color]) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <i className="inline-block size-2.5 rounded-sm" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div
            className="grid grid-cols-[72px_1fr_auto] items-center gap-3 text-sm"
            key={row.weekStart}
          >
            <time className="text-muted-foreground">{formatWeek(row.weekStart)}</time>
            <div className="flex h-3 overflow-hidden rounded-sm bg-muted">
              {COHORT_SEGMENTS.map(([key, label, color]) => {
                const value = row[key];
                if (!value || !row.total) return null;
                return (
                  <span
                    key={key}
                    className="h-full"
                    style={{ width: `${(value / row.total) * 100}%`, background: color }}
                    title={`${label}: ${value}`}
                  />
                );
              })}
            </div>
            <strong className="tabular-nums">{row.total}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function IneligibleChart({ rows }: { rows: DcmpBillingWeeklyIneligible[] }) {
  const maximum = Math.max(1, ...rows.map((row) => row.total));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {INELIGIBLE_SEGMENTS.map(([key, label, color]) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <i className="inline-block size-2.5 rounded-sm" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
      <div className="flex items-end gap-3 overflow-x-auto pb-1">
        {rows.map((row) => (
          <div className="flex min-w-14 flex-col items-center gap-1 text-xs" key={row.weekStart}>
            <strong className="tabular-nums text-foreground">{row.total || ""}</strong>
            <div
              className="flex w-8 flex-col-reverse overflow-hidden rounded-sm bg-muted"
              style={{ height: `${Math.max(12, (row.total / maximum) * 150)}px` }}
            >
              {INELIGIBLE_SEGMENTS.map(([key, label, color]) => {
                const value = row[key];
                if (!value || !row.total) return null;
                return (
                  <span
                    key={key}
                    style={{ height: `${(value / row.total) * 100}%`, background: color }}
                    title={`${label}: ${value}`}
                  />
                );
              })}
            </div>
            <time className="text-muted-foreground">{formatWeek(row.weekStart)}</time>
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardBody({ data }: { data: DcmpBillingSnapshotResponse }) {
  const trendRows = data.monthlyTrend;
  const firstTrend = trendRows[0];
  const prior = trendRows.at(-2);
  const latestTrend = trendRows.at(-1);
  const current = data.summary;
  const currentMonth = `${data.asOf.slice(0, 7)}-01`;
  const isPartialMonth = data.month === currentMonth;
  const populationTotal = data.guideStatuses.reduce((sum, row) => sum + row.patients, 0);
  const fourWeekAdds = data.weeklyCohorts.slice(-4).reduce((sum, row) => sum + row.total, 0);
  const latestCompleteIneligible = data.weeklyIneligible.at(-2);
  const stageTotals = data.rateDetails.reduce(
    (totals, row) => {
      const stage = row.patientStage === "new" ? "new" : "established";
      totals[stage].patients += row.patients;
      totals[stage].billables += row.netBillables;
      return totals;
    },
    {
      new: { patients: 0, billables: 0 },
      established: { patients: 0, billables: 0 },
    },
  );
  const rateMatchedPatients = stageTotals.new.patients + stageTotals.established.patients;
  const rateMatchedBillables = stageTotals.new.billables + stageTotals.established.billables;
  const unmatchedDetailEntries = data.rateDetails.reduce(
    (sum, row) => sum + row.unmatchedRateEntries,
    0,
  );

  let growthStreak = 0;
  for (let index = trendRows.length - 1; index > 0; index -= 1) {
    if ((trendRows[index]?.patients || 0) <= (trendRows[index - 1]?.patients || 0)) break;
    growthStreak += 1;
  }

  const trend = useMemo(
    () => ({
      labels: trendRows.map((row) => formatMonth(row.monthStart)),
      datasets: [
        {
          label: "Patients billed",
          data: trendRows.map((row) => row.patients),
          borderColor: COLORS.navy,
          backgroundColor: "rgba(0, 78, 137, .09)",
          yAxisID: "patients",
          fill: true,
          tension: 0.25,
          pointRadius: 4,
          pointBackgroundColor: COLORS.navy,
        },
        {
          label: "Net billables",
          data: trendRows.map((row) => row.netBillables),
          borderColor: COLORS.orange,
          backgroundColor: COLORS.orange,
          yAxisID: "billables",
          tension: 0.25,
          pointRadius: 4,
        },
      ],
    }),
    [trendRows],
  );

  const trendOptions = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true } },
        tooltip: {
          callbacks: {
            label(context: TooltipItem<"line">) {
              const raw = Number(context.raw || 0);
              return context.dataset.yAxisID === "billables"
                ? ` Net billables: ${money.format(raw)}`
                : ` Patients billed: ${integer.format(raw)}`;
            },
          },
        },
      },
      scales: {
        patients: {
          position: "left",
          beginAtZero: true,
          ticks: { precision: 0 },
          title: { display: true, text: "Patients" },
        },
        billables: {
          position: "right",
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: { callback: (value) => money.format(Number(value)) },
          title: { display: true, text: "Net billables" },
        },
        x: { grid: { display: false } },
      },
    }),
    [],
  );

  return (
    <>
      <p className="text-xs text-muted-foreground">
        Showing {data.clinic.name} · {formatMonth(data.month, true)} · patient statuses as of{" "}
        {data.asOf}
      </p>

      <section className="space-y-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Monthly summary
          </p>
          <p className="text-sm text-muted-foreground">
            Craniometrix DCMP billables for the selected month, excluding provider pass-through.
          </p>
        </div>
        <KpiGrid>
          <KpiCard
            label="Net DCMP billables"
            value={money.format(current.netBillables)}
            footnote="Craniometrix share after provider pass-through"
            delta={formatDelta(
              isPartialMonth ? null : delta(current.netBillables, prior?.netBillables),
              isPartialMonth ? "Month to date" : "No prior comparison",
            )}
            deltaDirection={
              (delta(current.netBillables, prior?.netBillables) ?? 0) >= 0 ? "up" : "down"
            }
          />
          <KpiCard
            label="Patients billed"
            value={integer.format(current.patients)}
            footnote="Unique DCMP patients in selected month"
            delta={formatDelta(
              isPartialMonth ? null : delta(current.patients, prior?.patients),
              isPartialMonth ? "Month to date" : "No prior comparison",
            )}
            deltaDirection={(delta(current.patients, prior?.patients) ?? 0) >= 0 ? "up" : "down"}
          />
          <KpiCard
            label="DCMP billing entries"
            value={integer.format(current.entries)}
            footnote="All DCMP services in the selected month"
            delta={isPartialMonth ? "Month to date" : "Selected month"}
          />
        </KpiGrid>
        {current.unmatchedRateEntries ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {integer.format(current.unmatchedRateEntries)} entries
            {current.unmatchedRateCodes.length
              ? ` (${current.unmatchedRateCodes.join(", ")})`
              : ""}{" "}
            have no configured net rate and are excluded from billable dollars across{" "}
            {integer.format(current.unmatchedRateClinics)}{" "}
            {current.unmatchedRateClinics === 1 ? "clinic" : "clinics"}. Patient and entry counts
            still include them.
          </div>
        ) : null}
      </section>

      <ChartPanel
        title={
          isPartialMonth
            ? `${formatMonth(data.month, true)} is still in progress.`
            : growthStreak > 0
              ? `DCMP patient volume has grown for ${growthStreak} consecutive ${growthStreak === 1 ? "month" : "months"}.`
              : "Monthly patients and net billables"
        }
      >
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-md border border-border/60 px-3 py-2">
            <p className="text-xs text-muted-foreground">Patients billed</p>
            <p className="text-lg font-semibold">{integer.format(latestTrend?.patients || 0)}</p>
            <p className="text-xs text-muted-foreground">
              {delta(latestTrend?.patients, prior?.patients) === null
                ? "No prior month"
                : `${(delta(latestTrend?.patients, prior?.patients) ?? 0) >= 0 ? "+" : ""}${(delta(latestTrend?.patients, prior?.patients) ?? 0).toFixed(1)}% from prior month`}
              {firstTrend
                ? ` · ${(delta(latestTrend?.patients, firstTrend.patients) ?? 0) >= 0 ? "+" : ""}${(delta(latestTrend?.patients, firstTrend.patients) ?? 0).toFixed(1)}% since ${formatMonth(firstTrend.monthStart)}`
                : ""}
            </p>
          </div>
          <div className="rounded-md border border-border/60 px-3 py-2">
            <p className="text-xs text-muted-foreground">Net DCMP billables</p>
            <p className="text-lg font-semibold">{money.format(latestTrend?.netBillables || 0)}</p>
            <p className="text-xs text-muted-foreground">
              {delta(latestTrend?.netBillables, prior?.netBillables) === null
                ? "No prior month"
                : `${(delta(latestTrend?.netBillables, prior?.netBillables) ?? 0) >= 0 ? "+" : ""}${(delta(latestTrend?.netBillables, prior?.netBillables) ?? 0).toFixed(1)}% from prior month`}
              {firstTrend
                ? ` · ${(delta(latestTrend?.netBillables, firstTrend.netBillables) ?? 0) >= 0 ? "+" : ""}${(delta(latestTrend?.netBillables, firstTrend.netBillables) ?? 0).toFixed(1)}% since ${formatMonth(firstTrend.monthStart)}`
                : ""}
            </p>
          </div>
        </div>
        <div className="h-72">
          <Line data={trend} options={trendOptions} />
        </div>
      </ChartPanel>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartPanel title="Where patients stand">
          <p className="mb-3 text-sm text-muted-foreground">
            Plain-language labels describe each patient’s latest GUIDE status.
          </p>
          <div className="mb-4 rounded-md border border-border/60 px-3 py-3">
            <p className="text-2xl font-semibold tabular-nums">{integer.format(populationTotal)}</p>
            <p className="text-sm text-muted-foreground">Total patients</p>
            <p className="text-xs text-muted-foreground">
              +{integer.format(fourWeekAdds)} added in the past four weeks
            </p>
          </div>
          <StatusSummary rows={data.guideStatuses} />
        </ChartPanel>
        <ChartPanel title="What happens after patients are added">
          <p className="mb-3 text-sm text-muted-foreground">
            Patients grouped by the week their Carespace was created, then colored by current
            outcome.
          </p>
          <CohortChart rows={data.weeklyCohorts} />
        </ChartPanel>
      </div>

      <ChartPanel title="Patients deemed ineligible each week">
        <p className="mb-3 text-sm text-muted-foreground">
          First recorded ineligible determination, grouped into actionable reason families.
        </p>
        <IneligibleChart rows={data.weeklyIneligible} />
        {latestCompleteIneligible ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {integer.format(latestCompleteIneligible.total)} patients were deemed ineligible in the
            latest complete week; {integer.format(latestCompleteIneligible.coverage)} were related
            to coverage.
          </p>
        ) : null}
      </ChartPanel>

      <ChartPanel title="What makes up this month’s billables">
        <p className="mb-3 text-sm text-muted-foreground">
          Rates are the Craniometrix-receivable portion of the latest configured clinic schedule.
          Provider pass-through is excluded.
        </p>
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-md border border-border/60 px-3 py-3">
            <p className="text-sm font-semibold text-foreground">Newly enrolled</p>
            <p className="text-xs text-muted-foreground">Under 6 months</p>
            <p className="mt-2 text-lg font-semibold tabular-nums">
              {integer.format(stageTotals.new.patients)}
            </p>
            <p className="text-sm text-muted-foreground">
              {money.format(stageTotals.new.billables)} net billables
            </p>
          </div>
          <div className="rounded-md border border-border/60 px-3 py-3">
            <p className="text-sm font-semibold text-foreground">Established</p>
            <p className="text-xs text-muted-foreground">6+ months</p>
            <p className="mt-2 text-lg font-semibold tabular-nums">
              {integer.format(stageTotals.established.patients)}
            </p>
            <p className="text-sm text-muted-foreground">
              {money.format(stageTotals.established.billables)} net billables
            </p>
          </div>
        </div>
        <DataTable
          columns={[
            {
              key: "patientStage",
              label: "Patient stage",
              render: (row) => (row.patientStage === "new" ? "Newly enrolled" : "Established"),
            },
            {
              key: "careContext",
              label: "Care context",
              render: (row) =>
                row.careContext === "caregiver_involved" ? "Caregiver involved" : "No caregiver",
            },
            { key: "tier", label: "Tier" },
            {
              key: "hcpcs",
              label: "Code",
              render: (row) => <code>{row.hcpcs}</code>,
            },
            {
              key: "averageRate",
              label: "Net rate",
              align: "right",
              render: (row) =>
                row.netBillables === 0 && row.unmatchedRateEntries > 0
                  ? "Not configured"
                  : money.format(row.averageRate),
            },
            {
              key: "patients",
              label: "Patients",
              align: "right",
              render: (row) => integer.format(row.patients),
            },
            {
              key: "unmatchedRateEntries",
              label: "Missing rates",
              align: "right",
              render: (row) => integer.format(row.unmatchedRateEntries),
            },
            {
              key: "netBillables",
              label: "Net billables",
              align: "right",
              render: (row) => <strong>{money.format(row.netBillables)}</strong>,
            },
          ]}
          rows={data.rateDetails}
          rowKey={(row, index) =>
            `${row.hcpcs}-${row.patientStage}-${row.tier}-${row.careContext}-${index}`
          }
          emptyMessage="No rate detail rows for this month."
        />
        <p className="mt-3 text-xs text-muted-foreground">
          DCMP detail total · {integer.format(rateMatchedPatients)} patients ·{" "}
          {integer.format(unmatchedDetailEntries)} missing rates ·{" "}
          {money.format(rateMatchedBillables)} net billables
        </p>
      </ChartPanel>

      <footer className="text-xs text-muted-foreground">
        Source: production DCMP billing entries, effective organization payment rates, Carespaces,
        and latest GUIDE enrollment status.
      </footer>
    </>
  );
}

export function DcmpBillingDashboard() {
  const [clinic, setClinic] = useState("all");
  const [month, setMonth] = useState("");
  const snapshotParams = month ? { clinic, month } : { clinic };
  const query = useQuery(dcmpBillingSnapshotQueryOptions(snapshotParams));

  return (
    <DashboardsPageFrame className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Finance · DCMP only
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Billing overview
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Net Craniometrix billables, patient growth, and GUIDE enrollment outcomes. Provider
            pass-through amounts and non-DCMP care are excluded.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className={
              query.data?.source === "live"
                ? "rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800"
                : query.data?.source === "mock"
                  ? "rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
                  : "rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground"
            }
          >
            {query.data
              ? query.data.source === "live"
                ? "Live data"
                : "Mock data"
              : "Loading data…"}
          </span>
          <div className="flex flex-wrap justify-end gap-2">
            <label className="text-xs font-medium text-muted-foreground">
              Clinic
              <select
                className="mt-1 block min-w-48 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                value={clinic}
                onChange={(event) => {
                  setClinic(event.target.value);
                  setMonth("");
                }}
              >
                <option value="all">All DCMP clinics</option>
                {(query.data?.clinics || []).map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                    {item.hasPaymentRates ? "" : " · rates missing"}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-muted-foreground">
              Billing month
              <select
                className="mt-1 block min-w-40 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                value={month || query.data?.month || ""}
                onChange={(event) => setMonth(event.target.value)}
              >
                {(query.data?.availableMonths || []).map((value) => (
                  <option value={value} key={value}>
                    {formatMonth(value, true)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </header>

      {query.isPending && !query.data ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading DCMP Billing…
        </div>
      ) : null}

      {query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : "DCMP Billing data is unavailable. Please try again."}
        </div>
      ) : null}

      {query.data ? <DashboardBody data={query.data} /> : null}
    </DashboardsPageFrame>
  );
}
